import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { TrackingService } from './tracking.service';

declare const fbq: any;
declare const gtag: any;

@Injectable({
  providedIn: 'root'
})
export class DynamicPixelsService {

  private metaPixelLoaded = false;
  private googleAnalyticsLoaded = false;
  private currentMetaPixelId: string | null = null;
  private currentGoogleAnalyticsId: string | null = null;

  constructor(
    private router: Router,
    private trackingService: TrackingService
  ) {
    // Set up router tracking after pixels are initialized
    this.setupRouterTracking();
  }

  /**
   * Initialize Meta Pixel with tenant configuration
   */
  initializeMetaPixel(pixelId: string): void {
    if (!pixelId || this.currentMetaPixelId === pixelId) {
      return; // Already initialized with this ID
    }

    console.log('[DynamicPixels] Initializing Meta Pixel:', pixelId);

    try {
      // Remove previous Meta Pixel if exists
      this.removeMetaPixel();

      // Load Meta Pixel script
      this.loadMetaPixelScript();

      // Initialize the pixel
      if (typeof fbq !== 'undefined') {
        fbq('init', pixelId);
        fbq('track', 'PageView');
        
        this.metaPixelLoaded = true;
        this.currentMetaPixelId = pixelId;
        console.log('[DynamicPixels] Meta Pixel initialized successfully');
      }
    } catch (error) {
      console.error('[DynamicPixels] Error initializing Meta Pixel:', error);
    }
  }

  /**
   * Initialize Google Analytics with tenant configuration
   */
  initializeGoogleAnalytics(analyticsId: string): void {
    if (!analyticsId || this.currentGoogleAnalyticsId === analyticsId) {
      return; // Already initialized with this ID
    }

    console.log('[DynamicPixels] Initializing Google Analytics:', analyticsId);

    try {
      // Remove previous Google Analytics if exists
      this.removeGoogleAnalytics();

      // Load Google Analytics script
      this.loadGoogleAnalyticsScript(analyticsId);

      // Initialize gtag
      if (typeof gtag !== 'undefined') {
        gtag('config', analyticsId, {
          custom_map: {
            'custom_parameter_1': 'nevent_customer_id'
          }
        });

        this.googleAnalyticsLoaded = true;
        this.currentGoogleAnalyticsId = analyticsId;
        console.log('[DynamicPixels] Google Analytics initialized successfully');
      }
    } catch (error) {
      console.error('[DynamicPixels] Error initializing Google Analytics:', error);
    }
  }

  /**
   * Track Meta Pixel events
   */
  /**
   * @param eventId Meta browser/server deduplication key. The backend sends every purchase to the
   *                Conversions API with event_id = the Nevent purchase id, so a browser Purchase
   *                MUST pass that same id here — Meta then discards the duplicate within 48h.
   *                Omitting it on a Purchase while server conversions are active double-counts.
   */
  trackMetaPixelEvent(eventName: string, parameters?: any, eventId?: string): void {
    if (this.metaPixelLoaded && typeof fbq !== 'undefined') {
      const trackingId = this.trackingService.getTrackingId();
      const eventParams = {
        ...parameters,
        custom_data: {
          nevent_customer_id: trackingId,
          ...parameters?.custom_data
        }
      };

      if (eventId) {
        fbq('track', eventName, eventParams, { eventID: eventId });
      } else {
        fbq('track', eventName, eventParams);
      }
      console.log(`[DynamicPixels] Meta Pixel event tracked: ${eventName}`, eventParams);
    }
  }

  /**
   * Track custom Meta Pixel events
   */
  trackMetaPixelCustomEvent(eventName: string, parameters?: any): void {
    if (this.metaPixelLoaded && typeof fbq !== 'undefined') {
      const trackingId = this.trackingService.getTrackingId();
      const eventParams = {
        ...parameters,
        nevent_customer_id: trackingId
      };

      fbq('trackCustom', eventName, eventParams);
      console.log(`[DynamicPixels] Meta Pixel custom event tracked: ${eventName}`, eventParams);
    }
  }

  /**
   * Track Google Analytics events
   */
  trackGoogleAnalyticsEvent(eventName: string, parameters?: any): void {
    if (this.googleAnalyticsLoaded && typeof gtag !== 'undefined') {
      const trackingId = this.trackingService.getTrackingId();
      const eventParams = {
        ...parameters,
        custom_parameter_1: trackingId
      };

      gtag('event', eventName, eventParams);
      console.log(`[DynamicPixels] Google Analytics event tracked: ${eventName}`, eventParams);
    }
  }

  /**
   * Track E-commerce events for both platforms
   */
  trackPurchase(value: number, currency: string = 'EUR', items?: any[], eventId?: string): void {
    const trackingId = this.trackingService.getTrackingId();

    // Meta Pixel Purchase — eventId (the Nevent purchase id) is REQUIRED for dedup against the
    // server-side Conversions API event, which uses the purchase id as its event_id.
    this.trackMetaPixelEvent('Purchase', {
      value: value,
      currency: currency,
      contents: items,
      custom_data: {
        nevent_customer_id: trackingId
      }
    }, eventId);

    // Google Analytics Purchase
    this.trackGoogleAnalyticsEvent('purchase', {
      transaction_id: trackingId + '_' + Date.now(),
      value: value,
      currency: currency,
      items: items
    });
  }

  trackAddToCart(value: number, currency: string = 'EUR', itemName?: string): void {
    const trackingId = this.trackingService.getTrackingId();

    // Meta Pixel Add to Cart
    this.trackMetaPixelEvent('AddToCart', {
      value: value,
      currency: currency,
      content_name: itemName,
      custom_data: {
        nevent_customer_id: trackingId
      }
    });

    // Google Analytics Add to Cart
    this.trackGoogleAnalyticsEvent('add_to_cart', {
      currency: currency,
      value: value,
      items: itemName ? [{ item_name: itemName, quantity: 1, price: value }] : []
    });
  }

  trackViewContent(contentName?: string, contentCategory?: string): void {
    // Meta Pixel View Content
    this.trackMetaPixelEvent('ViewContent', {
      content_name: contentName,
      content_category: contentCategory
    });

    // Google Analytics View Item
    this.trackGoogleAnalyticsEvent('view_item', {
      item_name: contentName,
      item_category: contentCategory
    });
  }

  /**
   * Private methods for script loading and management
   */
  private loadMetaPixelScript(): void {
    if (document.getElementById('facebook-pixel-script')) {
      return; // Already loaded
    }

    // Create base script
    const script = document.createElement('script');
    script.id = 'facebook-pixel-script';
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
    `;
    document.head.appendChild(script);

    // Add noscript for users without JavaScript
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `
      <img height="1" width="1" style="display:none"
           src="https://www.facebook.com/tr?id=PLACEHOLDER&ev=PageView&noscript=1"/>
    `;
    document.head.appendChild(noscript);
  }

  private loadGoogleAnalyticsScript(analyticsId: string): void {
    if (document.getElementById('google-analytics-script')) {
      return; // Already loaded
    }

    // Create gtag script
    const gtagScript = document.createElement('script');
    gtagScript.id = 'google-analytics-script';
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${analyticsId}`;
    document.head.appendChild(gtagScript);

    // Initialize gtag
    const initScript = document.createElement('script');
    initScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
    `;
    document.head.appendChild(initScript);
  }

  private removeMetaPixel(): void {
    const existingScript = document.getElementById('facebook-pixel-script');
    if (existingScript) {
      existingScript.remove();
    }
    this.metaPixelLoaded = false;
    this.currentMetaPixelId = null;
  }

  private removeGoogleAnalytics(): void {
    const existingScript = document.getElementById('google-analytics-script');
    if (existingScript) {
      existingScript.remove();
    }
    this.googleAnalyticsLoaded = false;
    this.currentGoogleAnalyticsId = null;
  }

  private setupRouterTracking(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Track page views in both platforms
        this.trackMetaPixelEvent('PageView');
        this.trackGoogleAnalyticsEvent('page_view', {
          page_path: event.urlAfterRedirects
        });
      });
  }

  /**
   * Get current configuration status
   */
  getStatus() {
    return {
      metaPixel: {
        loaded: this.metaPixelLoaded,
        pixelId: this.currentMetaPixelId
      },
      googleAnalytics: {
        loaded: this.googleAnalyticsLoaded,
        analyticsId: this.currentGoogleAnalyticsId
      }
    };
  }
}
